import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Programmes', href: '#programmes' },
    { label: 'Team', href: '#team' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#hero');
              }}
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/64hwalwg_Untitled%20design-5.png"
                alt="Green Lens Collective"
                className="h-16 md:h-20 w-auto group-hover:scale-105 transition-transform duration-300"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  className="text-sm font-medium tracking-wide uppercase text-[#1A2D23] hover:text-[#2E5C44] transition-colors duration-300"
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#partner"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#partner');
                }}
                className="rounded-full bg-[#2E5C44] text-white hover:bg-[#234734] px-6 py-3 text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="nav-partner-btn"
              >
                Partner With Us
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-[#F2F0E9] rounded-full transition-colors duration-300"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A2D23]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A2D23]" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-lg md:hidden"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  className="text-base font-medium text-[#1A2D23] hover:text-[#2E5C44] py-2 transition-colors duration-300"
                  data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#partner"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#partner');
                }}
                className="rounded-full bg-[#2E5C44] text-white hover:bg-[#234734] px-6 py-3 text-base font-medium text-center shadow-lg hover:shadow-xl transition-all duration-300 mt-2"
                data-testid="mobile-partner-btn"
              >
                Partner With Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;